import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ArrowLeft, Check, LogIn, PencilLine, ShoppingBag } from 'lucide-react';
import { RenameDialog } from '../components/RenameDialog.tsx';
import { BADGES } from '../../shared/badges.ts';
import { COSMETICS, owns, type CosmeticKind, type Look } from '../../shared/cosmetics.ts';
import type { InventoryResponse, ProfileResponse } from '../../shared/protocol.ts';
import { useAuth } from '../app/auth.ts';
import { usePreferences, useT } from '../app/preferences.ts';
import { Link } from '../components/Link.tsx';
import { CosmeticPreview, GoldAmount, PlayerAvatar, PlayerName } from '../components/PlayerLook.tsx';
import { useCosmeticName } from '../app/cosmeticName.ts';
import { fetchInventory, fetchProfile, saveLook } from '../online/progressionApi.ts';
import { useTransient } from '../hooks/useGameView.ts';
import type { MessageKey } from '../i18n/messages.ts';

/** Someone's public profile (/u/:name). */
export function ProfilePage({ username }: { username: string }) {
  const t = useT();
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileResponse | null | 'missing'>(null);

  useEffect(() => {
    fetchProfile(username)
      .then((p) => setProfile(p ?? 'missing'))
      .catch(() => setProfile('missing'));
  }, [username]);

  const mine = user?.username?.toLowerCase() === username.toLowerCase();

  return (
    <div className="page-fill contained profile-page">
      <div className="page-top">
        <Link href="/leaderboard" className="back-link">
          <ArrowLeft size={22} strokeWidth={2.5} /> {t('nav.leaderboard')}
        </Link>
      </div>
      {profile === null ? (
        <div className="center-state" role="status">
          <span className="spinner" />
        </div>
      ) : profile === 'missing' ? (
        <div className="center-state">
          <span className="result-icon emoji" aria-hidden="true">
            🔍
          </span>
          <p>{t('profile.notFound')}</p>
        </div>
      ) : (
        <>
          <ProfileCard profile={profile} />
          {mine && (
            <Link href="/me" className="btn btn-blue profile-edit-link">
              {t('profile.edit')}
            </Link>
          )}
        </>
      )}
    </div>
  );
}

/** The signed-in player's own profile, with the look editor. */
export function MyProfilePage() {
  const t = useT();
  const { user, canSignIn, signIn, refresh } = useAuth();
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [inventory, setInventory] = useState<InventoryResponse | null>(null);
  const [renaming, setRenaming] = useState(false);
  const username = user?.username ?? null;

  const reload = useCallback(() => {
    if (!username) return;
    void fetchProfile(username).then((p) => p && setProfile(p));
    void fetchInventory().then(setInventory);
  }, [username]);

  useEffect(reload, [reload]);

  if (!user?.username) {
    return (
      <div className="center-state pop-in">
        <span className="result-icon emoji" aria-hidden="true">
          🙂
        </span>
        <p>{t('board.signIn')}</p>
        {canSignIn && (
          <button type="button" className="btn btn-blue" onClick={() => signIn('/me')}>
            <LogIn size={22} /> {t('auth.signIn')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="page-fill contained profile-page">
      <div className="page-top">
        <Link href="/" className="back-link">
          <ArrowLeft size={22} strokeWidth={2.5} /> {t('nav.home')}
        </Link>
        <h1 className="page-title">{t('nav.profile')}</h1>
        <span className="badge badge-accent">
          <GoldAmount amount={inventory?.gold ?? user.gold} />
        </span>
      </div>
      <div className="profile-columns">
        {profile ? (
          <ProfileCard
            profile={{ ...profile, look: inventory?.look ?? profile.look }}
            nameAction={
              <button
                type="button"
                className="icon-btn rename-pencil"
                onClick={() => setRenaming(true)}
                aria-label={t('rename.button')}
                title={t('rename.button')}
              >
                <PencilLine size={22} />
              </button>
            }
          />
        ) : (
          <div className="card profile-card" />
        )}
        {inventory && (
          <LookEditor
            inventory={inventory}
            name={user.username}
            onSaved={(next) => {
              setInventory(next);
              void refresh();
            }}
          />
        )}
      </div>
      <RenameDialog open={renaming} onClose={() => setRenaming(false)} gold={inventory?.gold ?? user.gold} />
    </div>
  );
}

function ProfileCard({ profile, nameAction }: { profile: ProfileResponse; nameAction?: ReactNode }) {
  const { prefs, t } = usePreferences();
  const earned = new Map(profile.badges.map((b) => [b.id, b.earnedAt]));
  const joined = new Date(profile.joinedAt).toLocaleDateString(prefs.locale, { year: 'numeric', month: 'long' });

  return (
    <section className="card profile-card pop-in">
      <div className="profile-head">
        <PlayerAvatar look={profile.look} name={profile.username} size="xl" />
        <div className="profile-id">
          <div className="profile-name-row">
            <h1 className="profile-name">
              <PlayerName name={profile.username} look={profile.look} />
            </h1>
            {nameAction}
          </div>
          <div className="profile-tags">
            <span className="badge badge-accent">🏆 {profile.rating}</span>
            {profile.rank && <span className="badge">{t('profile.rank', { rank: profile.rank })}</span>}
            <span className="badge">{t('profile.best', { rating: profile.bestRating })}</span>
          </div>
          <p className="profile-joined">{t('profile.joined', { date: joined })}</p>
        </div>
      </div>

      <dl className="account-stats">
        <div>
          <dt>{t('account.games')}</dt>
          <dd>{profile.games}</dd>
        </div>
        <div>
          <dt>{t('account.wins')}</dt>
          <dd>{profile.wins}</dd>
        </div>
        <div>
          <dt>{t('account.draws')}</dt>
          <dd>{profile.draws}</dd>
        </div>
        <div>
          <dt>{t('account.losses')}</dt>
          <dd>{profile.losses}</dd>
        </div>
      </dl>

      <h2 className="section-title">
        {t('profile.badges')} <span className="badge">{earned.size}/{BADGES.length}</span>
      </h2>
      <ul className="badge-grid">
        {BADGES.map((b) => {
          const at = earned.get(b.id);
          return (
            <li
              key={b.id}
              className={`badge-tile${at ? '' : ' locked'}`}
              title={at ? new Date(at).toLocaleDateString(prefs.locale) : t('profile.locked')}
            >
              <span className="badge-emoji emoji" aria-hidden="true">
                {b.emoji}
              </span>
              <span className="badge-name">{t(`badge.${b.id}` as MessageKey)}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const KINDS: { kind: CosmeticKind; label: 'profile.avatar' | 'profile.frame' | 'profile.nameStyle'; key: keyof Look }[] = [
  { kind: 'avatar', label: 'profile.avatar', key: 'avatar' },
  { kind: 'frame', label: 'profile.frame', key: 'frame' },
  { kind: 'nameStyle', label: 'profile.nameStyle', key: 'nameStyle' },
];

function LookEditor({ inventory, name, onSaved }: { inventory: InventoryResponse; name: string; onSaved: (inv: InventoryResponse) => void }) {
  const t = useT();
  const itemName = useCosmeticName();
  const owned = new Set(inventory.owned);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const saved = useTransient(savedAt, 1800);

  const choose = async (key: keyof Look, value: string | null) => {
    if (saving) return;
    setSaving(true);
    const result = await saveLook({ ...inventory.look, [key]: value } as Look);
    setSaving(false);
    if (result.ok) {
      onSaved(result.data);
      setSavedAt((n) => (n ?? 0) + 1);
    }
  };

  return (
    <section className="card look-editor pop-in" aria-busy={saving}>
      {/* The profile card beside this already shows the result, so no second preview here. */}
      <div className="look-editor-head">
        <h2 className="look-editor-title">{t('profile.edit')}</h2>
        {saved && (
          <span className="badge badge-green">
            <Check size={16} /> {t('profile.saved')}
          </span>
        )}
      </div>

      {KINDS.map(({ kind, label, key }) => {
        const items = COSMETICS.filter((c) => c.kind === kind && owns(owned, c.id));
        const current = inventory.look[key];
        return (
          <div key={kind} className="field">
            <span className="field-label">{t(label)}</span>
            <div className="look-options" role="radiogroup" aria-label={t(label)}>
              {kind !== 'avatar' && (
                <button
                  type="button"
                  role="radio"
                  aria-checked={current === null}
                  className={`look-option none${current === null ? ' selected' : ''}`}
                  onClick={() => choose(key, null)}
                >
                  {t('profile.none')}
                </button>
              )}
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="radio"
                  aria-checked={current === item.id}
                  className={`look-option rarity-${item.rarity}${current === item.id ? ' selected' : ''}`}
                  onClick={() => choose(key, item.id)}
                  title={itemName(item.id)}
                  aria-label={itemName(item.id)}
                >
                  <CosmeticPreview id={item.id} name={name} look={inventory.look} />
                </button>
              ))}
            </div>
          </div>
        );
      })}

      <Link href="/market" className="btn btn-primary">
        <ShoppingBag size={20} /> {t('profile.toShop')}
      </Link>
    </section>
  );
}
